import {
  Box,
  Typography,
  Card,
  Stack,
  SxProps,
  Theme,
  svgIconClasses,
  TypographyProps,
  Skeleton,
  alpha
} from '@mui/material'
import React from 'react'

import {mergeSxs} from '../../utils'

const NON_BREAKABLE_SPACE = String.fromCharCode(160)

type Size = 'XL' | 'L' | 'M' | 'S' | 'XS'
type Variant = 'neutral' | 'positive' | 'negative' | 'warning' | 'no-value'

type KpiScoreProps = {
  label: string
  value: number | string | undefined
  type?: 'transparent' | 'boxed'
  variant?: Variant
  contrastMode?: boolean
  size?: Size
  unit?: string
  labelIcon?: React.ReactElement
  valueIcon?: React.ReactElement
  showValueLoader?: boolean
  transparentIconLabelSxProp?: SxProps<Theme>
  valueSxProp?: SxProps<Theme>
  'data-test-id'?: string
}

const valueStyles: Record<Size, SxProps<Theme>> = {
  XL: {fontSize: 64, lineHeight: '64px', fontWeight: 300},
  L: {fontSize: 36, lineHeight: '36px', fontWeight: 300},
  M: {fontSize: 28, lineHeight: '34px', fontWeight: 300},
  S: {fontSize: 22, lineHeight: '30px', fontWeight: 300},
  XS: {fontSize: 16, lineHeight: '24px', fontWeight: 500}
}

const valueAdormentSizes: Record<Size, SxProps<Theme>> = {
  XL: {width: 40, height: 40},
  L: {width: 32, height: 32},
  M: {width: 24, height: 24},
  S: {width: 16, height: 16},
  XS: {width: 16, height: 16}
}

const getAdormentStyles = (size: Size) => ({
  alignSelf: 'center',
  ...valueAdormentSizes[size],
  [`& .${svgIconClasses.root}`]: {
    ...valueAdormentSizes[size],
    fontSize: 'inherit',
    lineHeight: 'inherit'
  }
})

const getLabelStyles = (contrast: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  color: ({palette}) =>
    contrast ? alpha(palette.primary.contrastText, 0.6) : palette.text.secondary,
  [`& .${svgIconClasses.root}`]: {
    width: 16,
    height: 16
  }
})

const unitStyles: SxProps<Theme> = {
  fontSize: 12,
  lineHeight: '16px'
}

const variantColorMap: Record<Variant, TypographyProps['color']> = {
  neutral: 'inherit',
  positive: 'success.main',
  negative: 'error.main',
  warning: 'warning.main',
  'no-value': 'text.secondary'
}

const contrastVariantColorMap: Record<Variant, TypographyProps['color']> = {
  neutral: 'inherit',
  positive: 'success.light',
  negative: 'error.light',
  warning: 'warning.light',
  'no-value': 'text.secondary'
}

const colorMap = (contrast: boolean, variant: Variant): TypographyProps['color'] =>
  contrast ? contrastVariantColorMap[variant] : variantColorMap[variant]

export const KpiScore: React.FC<KpiScoreProps> = ({
  label,
  value,
  unit,
  type = 'transparent',
  variant = 'neutral',
  contrastMode = false,
  size = 'M',
  labelIcon,
  valueIcon,
  'data-test-id': dataTestId,
  showValueLoader = false,
  transparentIconLabelSxProp,
  valueSxProp
}) => {
  const valueElement = !showValueLoader ? (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
      {valueIcon && <Box sx={getAdormentStyles(size)}>{valueIcon}</Box>}
      <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.5}}>
        <Typography
          sx={mergeSxs(valueStyles[size], valueSxProp)}
          color={colorMap(contrastMode, variant)}
        >
          {value ?? NON_BREAKABLE_SPACE}
        </Typography>
        {unit && <Typography sx={unitStyles}>{unit}</Typography>}
      </Box>
    </Box>
  ) : (
    <Skeleton sx={valueStyles[size]} />
  )
  const labelElement = (
    <Typography variant="caption" sx={getLabelStyles(contrastMode)}>
      {labelIcon}
      {label}
    </Typography>
  )
  const transparentIconLabel = (
    <Box
      sx={{
        display: 'flex',
        alignContent: 'center',
        ...transparentIconLabelSxProp
      }}
    >
      <Typography variant="caption" sx={getLabelStyles(contrastMode)}>
        {labelIcon}
        {label}
      </Typography>
    </Box>
  )

  return type === 'transparent' ? (
    <Stack spacing={0.5} data-test-id={dataTestId} sx={{flex: 1}}>
      {transparentIconLabel}
      {valueElement}
    </Stack>
  ) : (
    <Card sx={{padding: size === 'XS' ? 1 : 2, boxShadow: 2}} data-test-id={dataTestId}>
      <Stack spacing={0.5} alignItems="center">
        {valueElement}
        {labelElement}
      </Stack>
    </Card>
  )
}
